import {Button, type ButtonProps} from '@famly/mf_input_button';
import i18next from 'i18next';
import {useBreakpoints} from '@famly/mf_util_breakpoints';
import {useAriaAndDataProps} from '@famly/mf_ts-utils_data-aria-props';
import React from 'react';

type ModalSecondaryButtonProps = {
    text?: ButtonProps['text'];
    icon?: ButtonProps['icon'];
    isDisabled?: ButtonProps['isDisabled'];
    onClick?: ButtonProps['onClick'];
};

/**
 * ModalSecondaryButton component
 *
 * The secondary action button for modals, typically used for cancellation or alternative actions.
 * Renders as a secondary button with responsive sizing based on viewport width.
 *
 * - On mobile: Regular size
 * - On tablet and larger: Compact size
 *
 * Should be used inside ModalFooterButtons.
 *
 * @param {ModalSecondaryButtonProps} props - Component props
 * @param {ButtonProps['text']} [props.text='Confirm'] - Button text
 * @param {ButtonProps['isDisabled']} [props.isDisabled] - Button disabled flag
 * @param {ButtonProps['icon']} [props.icon] - Optional icon to display in the button
 * @param {Buttonprops['onClick']} [props.onClick] - Optional onClick for the button
 *
 * @example
 * <ModalFooterButtons>
 *   <ModalSecondaryButton text="Cancel" onClick={handleCancel} />
 *   <ModalPrimaryButton text="Save" onClick={handleSave} />
 * </ModalFooterButtons>
 */
export const ModalSecondaryButton = ({
    text = i18next.t('mf.modalsDialogs_modal.src.translations.json.confirmButtonLabel'),
    icon,
    isDisabled,
    onClick,
    ...rest
}: ModalSecondaryButtonProps) => {
    const {isTabletPortraitAndLarger} = useBreakpoints();
    const dataAriaProps = useAriaAndDataProps(rest);

    return (
        <Button
            fullWidth
            size={isTabletPortraitAndLarger ? 'compact' : 'regular'}
            type="button"
            variant="tertiary"
            text={text}
            icon={icon}
            isDisabled={isDisabled}
            onClick={onClick}
            order={isTabletPortraitAndLarger ? 2 : 3}
            {...dataAriaProps}
        />
    );
};
